import type { Id, TicketListItem, TicketSort, TicketSortDir } from '../../api/types';
import { PriorityIcon } from '../../components/ticket/PriorityIcon';
import { StatusChip } from '../../components/ticket/StatusChip';
import { TypeIcon } from '../../components/ticket/TypeIcon';
import { Avatar } from '../../components/ui/Avatar';
import { formatInstantDate, formatRelative } from '../../lib/format';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { COLUMNS, SORT_OPTIONS, type ColumnDef } from './list-params';

/**
 * 🔴 THE LIST VIEW'S TABLE — Or's shot 7. Work · Assignee · Reporter · Priority · Status · Resolution
 * · Created · Updated.
 *
 * ⭐ THIS IS NOT THE BACKLOG WITH MORE COLUMNS. The backlog groups, sums and orders; this one exists
 * to FIND a ticket — one row per ticket, no nesting, no grouping, every field in the same place on
 * every row so the eye can run down a column. That is why a sub-task appears here as an ordinary row
 * rather than under its parent: at 486 rows the question is "where is APFS-441", and an answer that
 * hides it inside a collapsed parent is not one.
 *
 * 🔴 MOBILE IS A DIFFERENT COMPONENT, NOT A REFLOWED TABLE. Eight columns at 390px is either an
 * unreadable squeeze or a horizontal scrollbar, and this app does not do horizontal scrollbars. Below
 * `md` the same eight fields are drawn as a card with labelled pairs; from `md` up it is a real
 * `<table>` with a real `<thead>`.
 */

export function ListTable({
  items,
  sort,
  dir,
  onSort,
  selectedId,
  onSelect,
  resolutionLabel,
  dimmed,
}: {
  items: readonly TicketListItem[];
  sort: TicketSort;
  dir: TicketSortDir;
  onSort: (sort: TicketSort) => void;
  selectedId: Id | null;
  onSelect: (id: Id) => void;
  /** `null` when the resolution vocabulary could not be loaded — the raw code is shown instead. */
  resolutionLabel: (code: string | null) => string | null;
  dimmed: boolean;
}) {
  return (
    <div className={dimmed ? 'opacity-60' : ''}>
      {/* ── phone ─────────────────────────────────────────────────────────────────────────────── */}
      <ul className="m-0 list-none p-0 md:hidden">
        {items.map((item) => (
          <li key={item.id}>
            <Card
              item={item}
              selected={item.id === selectedId}
              onSelect={onSelect}
              resolutionLabel={resolutionLabel}
            />
          </li>
        ))}
      </ul>

      {/* ── desktop ───────────────────────────────────────────────────────────────────────────── */}
      <table className="hidden w-full border-collapse text-[13px] md:table">
        <thead>
          <tr className="border-b border-[color:var(--color-line)] bg-[color:var(--color-surface-sunken)] text-left">
            {COLUMNS.map((column) => (
              <HeaderCell
                key={column.id}
                column={column}
                sort={sort}
                dir={dir}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <Row
              key={item.id}
              item={item}
              selected={item.id === selectedId}
              onSelect={onSelect}
              resolutionLabel={resolutionLabel}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * ⛔ A COLUMN THE API CANNOT ORDER BY IS STILL NOT A BUTTON. It is plain text with a title that says
 * why — because a header that looks clickable and does nothing is a bug report, and a header that
 * looks clickable and re-sorts the current page is a worse one. Five of the eight became buttons
 * because the API grew the orderings, not because this rule was relaxed.
 *
 * ⭐ `aria-sort` carries the REAL direction. It used to be hard-coded `'descending'`, which was true
 * when `updated` was the only ordering and had no ascending form; leaving it hard-coded now would
 * announce the wrong direction to a screen reader on every ascending sort — the arrow and the
 * announcement must not be able to disagree.
 */
function HeaderCell({
  column,
  sort,
  dir,
  onSort,
}: {
  column: ColumnDef;
  sort: TicketSort;
  dir: TicketSortDir;
  onSort: (sort: TicketSort) => void;
}) {
  const active = column.sort !== null && column.sort === sort;

  if (column.sort === null) {
    return (
      <th
        scope="col"
        title={column.note}
        className="px-2 py-2 text-[11.5px] font-semibold tracking-wide text-[color:var(--color-ink-subtle)] uppercase"
      >
        {column.label}
      </th>
    );
  }

  const ascending = dir === 'asc';
  const detail = SORT_OPTIONS.find((option) => option.value === column.sort)?.detail;

  return (
    <th
      scope="col"
      aria-sort={active ? (ascending ? 'ascending' : 'descending') : 'none'}
      className="px-2 py-2 text-[11.5px] font-semibold tracking-wide uppercase"
    >
      <button
        type="button"
        onClick={() => onSort(column.sort as TicketSort)}
        aria-pressed={active}
        title={
          active
            ? `${detail}. Click to sort ${ascending ? 'descending' : 'ascending'}.`
            : `Order by ${column.label.toLowerCase()} — ${detail}`
        }
        className={`flex items-center gap-1 ${
          active ? 'text-[color:var(--color-ink)]' : 'text-[color:var(--color-ink-subtle)]'
        }`}
      >
        {column.label}
        {/* ⚠️ A glyph, not a colour: the active column has to be identifiable without relying on the
            two greys telling each other apart. */}
        <span aria-hidden="true">{active ? (ascending ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
}

function Row({
  item,
  selected,
  onSelect,
  resolutionLabel,
}: {
  item: TicketListItem;
  selected: boolean;
  onSelect: (id: Id) => void;
  resolutionLabel: (code: string | null) => string | null;
}) {
  const vocab = useVocabulary();
  const status = vocab.status(item.status);
  const isClosed = vocab.statuses.find((entry) => entry.code === item.status)?.is_final ?? false;

  return (
    <tr
      onClick={() => onSelect(item.id)}
      data-selected={selected || undefined}
      className={`cursor-pointer border-b border-[color:var(--color-line)] align-middle ${
        selected ? 'bg-sky-50 dark:bg-sky-950/40' : 'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]'
      }`}
    >
      {/* 🔴 `w-full max-w-0` IS THE WHOLE TRICK, and getting it half right is worse than not doing it.
          A table cell sizes to its content, so `truncate` inside one never truncates — the column
          just grows and pushes Updated off the screen. `max-w-0` makes the cell shrink-to-fit so the
          child can ellipsise; `w-full` then hands it every pixel the other seven columns do not
          want. ⚠️ WITHOUT `w-full` the cell collapses to the key and THE TITLE DISAPPEARS ENTIRELY —
          which is exactly what the first build of this table did. */}
      <td className="w-full max-w-0 px-2 py-1.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <TypeIcon type={vocab.type(item.type)} />
          <span
            className={`shrink-0 font-mono text-[12.5px] font-semibold text-[color:var(--color-accent)] ${
              isClosed ? 'line-through decoration-2' : ''
            }`}
          >
            {item.key}
          </span>
          <span className="truncate text-[color:var(--color-ink)]" title={item.title}>
            {item.title}
          </span>
        </span>
      </td>
      {/* ⭐ `whitespace-nowrap` on the narrow columns: they are short values, and letting "chalermphan"
          wrap to two lines makes every row in the table a different height, which is the thing that
          stops the eye running down a column. */}
      <td className="px-2 py-1.5 whitespace-nowrap">
        <Person name={item.assignee} />
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <Person name={item.reporter} />
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <span className="flex items-center gap-1">
          <PriorityIcon priority={vocab.priority(item.priority)} />
          <span className="text-[color:var(--color-ink-muted)]">
            {vocab.priority(item.priority)?.label ?? '—'}
          </span>
        </span>
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap">
        <StatusChip status={status} />
      </td>
      <td className="px-2 py-1.5 whitespace-nowrap text-[color:var(--color-ink-muted)]">
        <Resolution code={item.resolution} resolutionLabel={resolutionLabel} />
      </td>
      <td
        className="px-2 py-1.5 whitespace-nowrap text-[color:var(--color-ink-muted)]"
        title={item.created_at}
      >
        {/* ⭐ An INSTANT (TIMESTAMPTZ), shown date-only — stays in the reader's local time. */}
        {formatInstantDate(item.created_at)}
      </td>
      <td
        className="px-2 py-1.5 whitespace-nowrap text-[color:var(--color-ink-muted)]"
        title={item.updated_at}
      >
        {formatRelative(item.updated_at)}
      </td>
    </tr>
  );
}

function Card({
  item,
  selected,
  onSelect,
  resolutionLabel,
}: {
  item: TicketListItem;
  selected: boolean;
  onSelect: (id: Id) => void;
  resolutionLabel: (code: string | null) => string | null;
}) {
  const vocab = useVocabulary();
  const isClosed = vocab.statuses.find((entry) => entry.code === item.status)?.is_final ?? false;

  return (
    <div
      onClick={() => onSelect(item.id)}
      data-selected={selected || undefined}
      className={`border-b border-[color:var(--color-line)] px-3 py-2.5 ${
        selected ? 'bg-sky-50 dark:bg-sky-950/40' : ''
      }`}
    >
      <div className="flex min-w-0 items-start gap-1.5">
        <span className="mt-[3px]">
          <TypeIcon type={vocab.type(item.type)} />
        </span>
        <span
          className={`shrink-0 font-mono text-[12.5px] font-semibold text-[color:var(--color-accent)] ${
            isClosed ? 'line-through decoration-2' : ''
          }`}
        >
          {item.key}
        </span>
        <span className="line-clamp-2 min-w-0 text-[13px] leading-snug text-[color:var(--color-ink)]">
          {item.title}
        </span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-[color:var(--color-ink-muted)]">
        <StatusChip status={vocab.status(item.status)} />
        <span className="flex items-center gap-1">
          <PriorityIcon priority={vocab.priority(item.priority)} />
          {vocab.priority(item.priority)?.label ?? '—'}
        </span>
        <span className="flex items-center gap-1">
          <Avatar name={item.assignee} size={18} />
          {item.assignee ?? 'Unassigned'}
        </span>
      </div>

      {/* ⭐ The four fields a card would otherwise drop. A phone layout that shows fewer FACTS than
          the desktop one is a different screen, not a responsive one — so Reporter, Resolution,
          Created and Updated are all here, as labelled pairs. */}
      <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-[color:var(--color-ink-subtle)]">
        <Pair label="Reporter" value={item.reporter} />
        <Pair
          label="Resolution"
          value={<Resolution code={item.resolution} resolutionLabel={resolutionLabel} />}
        />
        <Pair label="Created" value={formatInstantDate(item.created_at)} />
        <Pair label="Updated" value={formatRelative(item.updated_at)} />
      </dl>
    </div>
  );
}

function Pair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex min-w-0 gap-1">
      <dt className="shrink-0 font-semibold">{label}</dt>
      <dd className="m-0 min-w-0 truncate">{value}</dd>
    </div>
  );
}

function Person({ name }: { name: string | null }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Avatar name={name} size={20} />
      <span className="truncate text-[color:var(--color-ink-muted)]">{name ?? 'Unassigned'}</span>
    </span>
  );
}

/**
 * ⚠️ `null` resolution renders `—`, which is the truth: db/007 adds the column as nullable and a
 * ticket that has not shipped has no delivery route yet. ⛔ An unresolvable CODE renders the code
 * itself with a title saying the label is missing — never a title-cased guess, for the same reason
 * `Labelled.resolved` exists.
 */
function Resolution({
  code,
  resolutionLabel,
}: {
  code: string | null;
  resolutionLabel: (code: string | null) => string | null;
}) {
  if (!code) return <>—</>;
  const label = resolutionLabel(code);
  if (label) return <>{label}</>;
  return (
    <span title={`Resolution code ${code} — its label could not be loaded`} className="font-mono">
      {code}
    </span>
  );
}
